import './SearchMobile.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SearchInput } from '../SearchInput';
import { PerfUtilService } from '../../../services/util/perf-util.service';
import { useActiveStateWithTimeoutHook } from '../../../hooks/use-active-state-with-timeout.hook';
import { SessionEndConfirmPopup } from '../../SessionEndConfirmPopup';
import { SearchResults } from '../SearchResults/SearchResults';
import { LoadGameConfirmContext } from '../contexts/load-game-confirm.context';
import { useGameSearch } from '../hooks/use-game-search.hook';
import { useAsgActions } from '../../../hooks/use-asg-actions.hook';

export const SearchMobile = ({
    openGameWithConfirm = true,
}) => {
    const dispatch = useDispatch();
    const { fillCurrentGameSearchStatus } = useAsgActions();
    const [searchQuery, setSearchQuery] = useState('');
    const [confirmPopupGame, setConfirmPopupGame] = useState(null);
    const [searchResultWindowProps, setSearchResultWindowProps] = useState({});
    const { searchResult } = useGameSearch(searchQuery);
    const {
        isActive: popupActive,
        deactivate: closePopup,
        activate: activatePopup,
    } = useActiveStateWithTimeoutHook({
        timeoutMs: 7_000,
    });

    const onSearchClose = useCallback(() => {
        dispatch(fillCurrentGameSearchStatus(false));
    }, []);

    // calculate search window size required for react-window
    useEffect(() => {
        const searchInputHeightPx = 64;
        const searchResultsInfoHeightPx = 52;

        const setSearchSizes = PerfUtilService.throttle(() => {
            setSearchResultWindowProps({
                height: window.innerHeight - searchInputHeightPx - searchResultsInfoHeightPx,
                width: window.innerWidth,
            });
        }, 300);

        setSearchSizes();

        window.addEventListener('resize', setSearchSizes);

        return () => {
            window.removeEventListener('resize', setSearchSizes);
        };
    }, []);

    const onSearchQueryChange = useCallback(
        PerfUtilService.debounce((searchQueryUpdated) => {
            setSearchQuery(searchQueryUpdated);
        }, 300),
        [],
    );

    const onGameChange = useCallback(() => {
        closePopup();
        onSearchClose();
    }, []);

    const searchConfig = useMemo(() => ({
        openGameWithConfirm,
        activateConfirmPopup: (game) => {
            setConfirmPopupGame(game);
            activatePopup();
        },
    }), []);

    return (
        <div className="search-mobile">
            <div className="search-mobile__header">
                <div
                    className="search-mobile__close"
                    onClick={onSearchClose}
                />
                <div className="search-mobile__input">
                    <SearchInput
                        onSearchQueryChange={onSearchQueryChange}
                        focusOnSearchActivation={true}
                    />
                </div>
            </div>

            <div className="search-mobile__results">
                <LoadGameConfirmContext.Provider value={searchConfig}>
                    <SearchResults
                        searchResult={searchResult}
                        searchQuery={searchQuery}
                        searchWindowDimensions={searchResultWindowProps}
                    />
                </LoadGameConfirmContext.Provider>
            </div>

            {popupActive && confirmPopupGame && <>
                <SessionEndConfirmPopup
                    gameId={confirmPopupGame.id}
                    gameName={confirmPopupGame.name}
                    onConfirmClickHandler={onGameChange}
                />
                <div
                    className="search-mobile__overlay"
                    onClick={closePopup}
                />
            </>}
        </div>
    );
};
